import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, Activity, Trophy, BrainCircuit, Heart, Cpu, Shield } from "lucide-react";
import { ASSETS } from "../lib/assets";
import { Overline, Reveal, Stat } from "../components/Section";

const pillars = [
  { letter: "P", word: "Progress", desc: "Pushing boundaries with continuous innovation." },
  { letter: "J", word: "Journey", desc: "Every product is a story of growth and impact." },
  { letter: "S", word: "Strength", desc: "Resilient platforms built to scale globally." },
  { letter: "R", word: "Rewards", desc: "Value created flows back to our users." },
  { letter: "S", word: "Sustainability", desc: "Long-term thinking in every line of code." },
];

const focusAreas = [
  { icon: BrainCircuit, label: "AI-Powered Mobile Applications" },
  { icon: Heart, label: "Health & Wellness Platforms" },
  { icon: Trophy, label: "Sports & Talent Recognition" },
  { icon: Sparkles, label: "Digital Entertainment Products" },
];

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-slate-950 text-white">
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={ASSETS.images.heroBg} alt="" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/85 to-slate-950" />
          <div className="absolute inset-0 grid-bg opacity-40" />
          <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-teal-500/20 blur-[120px]" />
          <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-cyan-500/15 blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <Overline>AI · Innovation · Human Potential</Overline>
              </Reveal>
              <Reveal delay={0.1}>
                <h1 className="mt-6 font-display font-black text-[clamp(2.5rem,6vw,5rem)] leading-[0.95] tracking-tighter">
                  <span className="block text-white">Inspire.</span>
                  <span className="block text-gradient-teal">Empower.</span>
                  <span className="block text-white">Entertain.</span>
                </h1>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-8 max-w-xl text-lg text-slate-300 leading-relaxed">
                  PJSRS Infotech Private Limited is an AI-first technology company building intelligent digital products
                  that promote healthier lifestyles, celebrate talent, and create engaging entertainment
                  experiences.
                </p>
              </Reveal>

              <Reveal delay={0.3}>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link
                    to="/products"
                    data-testid="hero-cta-products"
                    className="inline-flex items-center gap-2 rounded-full bg-teal-400 text-slate-950 px-7 py-4 text-sm font-bold hover:bg-teal-300 transition-all shadow-[0_0_32px_rgba(45,212,191,0.35)] hover:-translate-y-0.5"
                  >
                    Explore our products <ArrowUpRight className="h-4 w-4" />
                  </Link>
                  <Link
                    to="/about"
                    data-testid="hero-cta-about"
                    className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-7 py-4 text-sm font-medium text-white hover:bg-white/10 transition-all"
                  >
                    The PJSRS story
                  </Link>
                </div>
              </Reveal>

              <Reveal delay={0.45}>
                <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
                  <Stat value="AI" suffix="-first" label="Engineering DNA" />
                  <Stat value="2" suffix="" label="Flagship products" />
                  <Stat value="5" suffix="" label="Guiding pillars" />
                  <Stat value="∞" suffix="" label="Lives to impact" />
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5 relative">
              <Reveal delay={0.2}>
                <div className="relative">
                  <div className="absolute -inset-10 bg-gradient-to-br from-teal-500/20 via-cyan-500/10 to-transparent blur-3xl" />
                  <div className="relative rounded-3xl bg-white/95 p-10 shadow-2xl ring-1 ring-white/10">
                    <img
                      src={ASSETS.logos.pjsrs}
                      alt="PJSRS Infotech logo"
                      className="w-full max-w-sm mx-auto"
                    />
                  </div>
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -top-6 -right-6 rounded-2xl bg-slate-900/90 backdrop-blur-md ring-1 ring-teal-400/30 px-4 py-3 shadow-xl"
                  >
                    <div className="flex items-center gap-2">
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75 animate-pulse-glow" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-teal-400" />
                      </span>
                      <span className="font-mono text-[11px] tracking-wider text-teal-300">LIVE · AI-FIRST</span>
                    </div>
                  </motion.div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE STAND FOR */}
      <section className="relative py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <Overline>What we stand for</Overline>
              <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-[1.05]">
                Three values.<br />
                <span className="text-gradient-teal">One mission.</span>
              </h2>
              <p className="mt-6 text-slate-400 leading-relaxed">
                Every product we ship is engineered around three ideas — to make lives better, to lift talent,
                and to make digital experiences worth coming back to.
              </p>
            </div>
            <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { icon: Heart, title: "Inspire", lines: ["Encourage healthy habits", "Motivate personal growth"] },
                { icon: Trophy, title: "Empower", lines: ["Recognise emerging talent", "Create opportunities"] },
                { icon: Sparkles, title: "Entertain", lines: ["Engaging digital products", "Communities around sport & stories"] },
              ].map((card, i) => (
                <Reveal key={card.title} delay={i * 0.08}>
                  <div className="group relative h-full rounded-2xl border border-white/10 bg-white/[0.02] p-7 backdrop-blur-sm hover:bg-white/[0.04] transition-all hover:-translate-y-1">
                    <div className="h-12 w-12 rounded-xl bg-teal-400/10 border border-teal-400/20 grid place-items-center text-teal-300 mb-6 group-hover:bg-teal-400/20 transition-colors">
                      <card.icon className="h-5 w-5" />
                    </div>
                    <div className="font-display text-2xl font-bold text-white">{card.title}</div>
                    <ul className="mt-4 space-y-2 text-sm text-slate-400">
                      {card.lines.map((l) => <li key={l}>· {l}</li>)}
                    </ul>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="relative py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <Overline>Our products</Overline>
              <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter">
                Three worlds. One vision.
              </h2>
            </div>
            <Link to="/products" data-testid="home-link-all-products" className="inline-flex items-center gap-2 text-sm text-teal-300 hover:text-teal-200">
              View all products <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Fit2Fin card */}
            <Reveal>
              <Link
                to="/fit2fin"
                data-testid="home-card-fit2fin"
                className="group relative block overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-emerald-500/10 via-slate-900 to-sky-500/10 p-8 md:p-10 min-h-[440px]"
              >
                <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-emerald-500/20 blur-[100px] group-hover:bg-emerald-500/30 transition" />
                <div className="relative">
                  <div className="flex items-center gap-4">
                    <img src={ASSETS.logos.fit2fin} alt="Fit2Fin" className="h-16 w-16 rounded-2xl shadow-xl ring-1 ring-white/10" />
                    <div>
                      <Overline color="text-emerald-300">India's First Habit Platform</Overline>
                      <div className="font-display text-3xl font-black mt-1">Fit2Fin<sup className="text-xs font-bold align-super text-slate-400">™</sup></div>
                    </div>
                  </div>
                  <h3 className="mt-10 font-display text-3xl md:text-4xl font-black tracking-tight leading-[1.05]">
                    Every <span className="text-gradient-fit">Healthy Habit</span><br />Deserves a <span className="text-gradient-fit">Reward</span>
                  </h3>
                  <p className="mt-5 max-w-md text-slate-300">
                    India's AI-powered habit & wellness rewards platform. Build healthy habits, earn Fit Points, and unlock everyday benefits through the Fit2Fin Rewards Ecosystem.
                  </p>
                  <div className="mt-8 flex flex-wrap gap-2">
                    {["Habit Building", "Fit Points", "AI Insights", "Rewards Marketplace"].map((t) => (
                      <span key={t} className="rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-xs text-slate-200">{t}</span>
                    ))}
                  </div>
                  <div className="mt-10 inline-flex items-center gap-2 text-emerald-300 font-medium group-hover:text-emerald-200">
                    Explore Fit2Fin <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            </Reveal>

            {/* CricStoryLive card */}
            <Reveal delay={0.1}>
              <Link
                to="/cricstorylive"
                data-testid="home-card-cricstory"
                className="group relative block overflow-hidden rounded-3xl border border-yellow-500/20 bg-gradient-to-br from-zinc-950 via-zinc-900 to-black p-8 md:p-10 min-h-[440px]"
              >
                <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-yellow-500/15 blur-[100px] group-hover:bg-yellow-500/25 transition" />
                <div className="relative">
                  <div className="flex items-center gap-4">
                    <img src={ASSETS.logos.cricstorylive} alt="CricStoryLive" className="h-16 w-16 rounded-2xl ring-1 ring-yellow-500/20 bg-black object-contain" />
                    <div>
                      <Overline color="text-yellow-400">AI-Powered Digital Cricket Ecosystem</Overline>
                      <div className="font-display text-3xl font-black mt-1 text-white">CricStoryLive</div>
                    </div>
                  </div>
                  <h3 className="mt-10 font-display text-3xl md:text-4xl font-black tracking-tight leading-[1.05]">
                    Every Run<br />
                    <span className="text-gradient-gold">Tells a Story.</span>
                  </h3>
                  <p className="mt-5 max-w-md text-slate-300">
                    An AI-powered digital cricket ecosystem that celebrates every player, every match, and
                    every story — from local playgrounds to international stadiums.
                  </p>
                  <div className="mt-8 flex flex-wrap gap-2">
                    {["Live Cricket", "Player Stories", "Grassroots Talent", "AI Match Intelligence"].map((t) => (
                      <span key={t} className="rounded-full bg-yellow-500/10 border border-yellow-500/20 px-3 py-1.5 text-xs text-yellow-200">{t}</span>
                    ))}
                  </div>
                  <div className="mt-10 inline-flex items-center gap-2 text-yellow-400 font-medium group-hover:text-yellow-300">
                    Explore CricStoryLive <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            </Reveal>

            {/* CrazyCricket card — Coming Soon (announcement only, no product page) */}
            <div className="lg:col-span-2">
              <Reveal delay={0.15}>
                <div
                  data-testid="home-card-crazycricket"
                  className="relative block overflow-hidden rounded-3xl border border-lime-500/20 bg-gradient-to-br from-lime-500/10 via-slate-900 to-emerald-500/10 p-8 md:p-10"
                >
                <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-lime-500/15 blur-[100px]" />
                <div className="relative flex flex-col md:flex-row md:items-center gap-8 md:gap-12">
                  <div className="flex items-center gap-4 md:min-w-[320px]">
                    <div className="h-16 w-16 rounded-2xl bg-white ring-1 ring-white/10 shadow-xl overflow-hidden grid place-items-center shrink-0">
                      <img src={ASSETS.logos.crazycricket} alt="CrazyCricket" className="h-full w-full object-cover" />
                    </div>
                    <div>
                      <Overline color="text-lime-300">A Cricket IQ Platform</Overline>
                      <div className="font-display text-3xl font-black mt-1 text-white">CrazyCricket</div>
                    </div>
                  </div>
                  <p className="text-slate-300 md:flex-1">Product details coming soon.</p>
                  <span
                    data-testid="home-crazycricket-cta"
                    className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-lime-500/30 px-5 py-2.5 text-sm font-medium text-lime-300 cursor-default select-none self-start md:self-center"
                  >
                    Details Coming Soon
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-lime-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-lime-400" />
                    </span>
                  </span>
                </div>
                </div>
              </Reveal>
            </div>

            {/* SalaryAI card — hidden until launch
            <Reveal delay={0.2}>
              <Link
                to="/salaryai"
                data-testid="home-card-salaryai"
                className="group relative block overflow-hidden rounded-3xl border border-blue-500/20 bg-gradient-to-br from-blue-600/10 via-slate-900 to-emerald-500/10 p-8 md:p-10 min-h-[440px]"
              >
                <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-blue-500/20 blur-[100px] group-hover:bg-blue-500/30 transition" />
                <div className="relative">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-2xl bg-white ring-1 ring-white/10 grid place-items-center p-2 shadow-xl">
                      <img src={ASSETS.logos.salaryai} alt="SalaryAI" className="h-full w-full object-contain" />
                    </div>
                    <div>
                      <Overline color="text-blue-300">Salary × Tax × Career</Overline>
                      <div className="font-display text-3xl font-black mt-1">SalaryAI</div>
                    </div>
                  </div>
                  <h3 className="mt-10 font-display text-2xl md:text-3xl font-black tracking-tight leading-[1.1]">
                    Know Your <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-emerald-400">Salary.</span><br />
                    Secure Your <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-emerald-400">Career.</span>
                  </h3>
                  <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-blue-500/10 border border-blue-500/30 px-3 py-1.5 text-[11px] text-blue-300 font-mono uppercase tracking-widest">
                    Coming soon
                  </div>
                  <div className="mt-10 inline-flex items-center gap-2 text-blue-300 font-medium group-hover:text-blue-200">
                    View product <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            </Reveal>
            */}
          </div>
        </div>
      </section>

      {/* OUR COMMITMENT */}
      <section className="relative py-28 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 grid-bg opacity-15" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[800px] rounded-full bg-teal-500/15 blur-[140px]" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="text-center">
              <div className="inline-flex items-center gap-2 rounded-full bg-teal-400/15 border border-teal-400/40 px-4 py-2 text-xs text-teal-300 font-mono uppercase tracking-[0.25em] mb-8">
                <Heart className="h-3.5 w-3.5" /> Our Commitment
              </div>
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-[1.05]">
                Every PJSRS product is guided<br />
                by <span className="text-gradient-teal">two questions.</span>
              </h2>
            </div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Reveal delay={0.1}>
              <div className="relative h-full rounded-3xl border border-teal-400/30 bg-gradient-to-br from-teal-950/60 via-slate-900 to-slate-950 p-10">
                <div className="absolute -top-6 -left-1 font-display text-[110px] leading-none font-black text-teal-400/15 select-none">01</div>
                <div className="relative">
                  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-teal-400 mb-4">Question One</div>
                  <p className="font-display text-xl md:text-2xl font-bold tracking-tight leading-[1.25] text-white">
                    How can <span className="text-gradient-teal">Artificial Intelligence</span> make this
                    product smarter, more effective, and more accessible?
                  </p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="relative h-full rounded-3xl border border-teal-400/30 bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950/60 p-10">
                <div className="absolute -top-6 -right-1 font-display text-[110px] leading-none font-black text-teal-400/15 select-none">02</div>
                <div className="relative">
                  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-teal-400 mb-4">Question Two</div>
                  <p className="font-display text-xl md:text-2xl font-bold tracking-tight leading-[1.25] text-white">
                    How can this product <span className="text-gradient-teal">positively impact</span>{" "}
                    people&apos;s lives and contribute to their personal growth, well-being, or happiness?
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.3}>
            <p className="mt-12 max-w-3xl mx-auto text-center text-slate-300 leading-relaxed">
              These principles define <span className="text-white font-semibold">who we are</span>,{" "}
              <span className="text-white font-semibold">what we build</span>, and the{" "}
              <span className="text-white font-semibold">future we strive to create</span>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative py-28 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <Overline>P · J · S · R · S</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter">
              Five letters.<br />
              <span className="text-gradient-teal">A philosophy.</span>
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-5 gap-px bg-white/5 border border-white/10 rounded-3xl overflow-hidden">
            {pillars.map((p, i) => (
              <Reveal key={p.word} delay={i * 0.05}>
                <div className="bg-slate-950 p-8 h-full hover:bg-slate-900/70 transition-colors">
                  <div className="font-display font-black text-6xl text-teal-400/90 leading-none">{p.letter}</div>
                  <div className="mt-6 font-display text-xl font-bold text-white">{p.word}</div>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOCUS AREAS */}
      <section className="relative py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <Overline>Focus areas</Overline>
              <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter">
                Built on AI.<br />
                <span className="text-gradient-teal">Crafted for impact.</span>
              </h2>
              <p className="mt-6 text-slate-400 leading-relaxed">
                Artificial Intelligence isn't a feature for us — it's the foundation of how we innovate,
                develop products, optimise operations, personalise experiences and scale businesses.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {focusAreas.map((a, i) => (
                <Reveal key={a.label} delay={i * 0.06}>
                  <div className="group flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.05] transition-all">
                    <div className="h-11 w-11 rounded-xl bg-teal-400/10 border border-teal-400/20 grid place-items-center text-teal-300 shrink-0">
                      <a.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-display text-lg font-bold text-white">{a.label}</div>
                      <div className="text-xs text-slate-500 mt-1 font-mono uppercase tracking-wider">— powered by AI</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 via-teal-950/40 to-slate-900 p-10 md:p-16">
            <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-teal-500/25 blur-[120px]" />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
              <div className="md:col-span-8">
                <Overline>Let's build the future, together</Overline>
                <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
                  Partner with PJSRS.
                </h2>
                <p className="mt-5 max-w-xl text-slate-300">
                  Investors, brand partners, wellness providers and talent organisations — let's create
                  meaningful impact across millions of lives.
                </p>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <Link
                  to="/contact"
                  data-testid="home-cta-contact"
                  className="inline-flex items-center gap-2 rounded-full bg-teal-400 text-slate-950 px-7 py-4 text-sm font-bold hover:bg-teal-300 transition-all shadow-[0_0_32px_rgba(45,212,191,0.35)] hover:-translate-y-0.5"
                >
                  Start a conversation <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
